import { useState, useEffect, useCallback, useRef } from 'react';
import { getSecurityStats, getActiveUsers, getLoginHistory } from './api';

// Poll cadence: backend throttles last_activity_at writes to once per 60s,
// so anything faster than 30s adds no fidelity.
export const POLL_INTERVAL = 30000;

function useList(fetcher, { page, pageSize, filters, pollMs }) {
    const empty = { rows: [], total: 0, page: 1, pageSize, totalPages: 0 };
    const [data, setData] = useState(empty);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchIdRef = useRef(0);

    const fetch = useCallback(async (silent = false) => {
        const id = ++fetchIdRef.current;
        if (!silent) setLoading(true);
        setError(null);
        try {
            const params = { page, pageSize, ...filters };
            Object.keys(params).forEach((k) => {
                if (params[k] === '' || params[k] === undefined || params[k] === null) delete params[k];
            });
            const res = await fetcher(params);
            if (id === fetchIdRef.current) setData(res.data || empty);
        } catch (err) {
            if (id === fetchIdRef.current) setError(err.message || 'Failed to load data');
        } finally {
            if (id === fetchIdRef.current && !silent) setLoading(false);
        }
    }, [page, pageSize, JSON.stringify(filters)]);

    useEffect(() => { fetch(); }, [fetch]);

    useEffect(() => {
        if (!pollMs) return undefined;
        const timer = setInterval(() => fetch(true), pollMs);
        return () => clearInterval(timer);
    }, [fetch, pollMs]);

    return { data, loading, error, refetch: fetch };
}

export function useActiveUsers({ page, pageSize, filters }) {
    return useList(getActiveUsers, {
        page, pageSize, filters, pollMs: POLL_INTERVAL,
    });
}

export function useLoginHistory({ page, pageSize, filters }) {
    return useList(getLoginHistory, { page, pageSize, filters });
}

export function useSecurityStats() {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const fetchIdRef = useRef(0);

    const fetch = useCallback(async () => {
        const id = ++fetchIdRef.current;
        try {
            const res = await getSecurityStats();
            if (id === fetchIdRef.current) {
                setStats(res.data || null);
                setError(null);
            }
        } catch (err) {
            if (id === fetchIdRef.current) setError(err.message || 'Failed to load stats');
        }
    }, []);

    useEffect(() => {
        fetch();
        const timer = setInterval(fetch, POLL_INTERVAL);
        return () => clearInterval(timer);
    }, [fetch]);

    return { stats, error, refetch: fetch };
}
